/**
 * Collection of commonly used string helpers
 *
 * Inspired by: https://gist.github.com/jonlabelle/5375315
 * @TODO: Create functional library
 */

/* -------------------------------- Constants ------------------------------- */

/**
 * STRING_PLACEHOLDER_REGEX
 *
 * Can be used to find the placeholders eg. `{0}`, `{2}`, `{34}`, etc...
 */
export const STRING_PLACEHOLDER_REGEX = /\{\d+\}/;

/* ---------------- Functional String.prototype replacements ---------------- */

/**
 * lowerCase
 *
 * Functional replacement for String.toLowerCase
 *
 * @param str - String to perform the operation on
 * @returns String transformed to lower case
 */
export const lowerCase = (str: string): string => str.toLowerCase();

/**
 * upperCase
 *
 * Functional replacement for String.toUpperCase
 *
 * @param str - String to perform the operation on
 * @returns String transformed to upper case
 */
export const upperCase = (str: string): string => str.toUpperCase();

/**
 * slice
 *
 * Functional replacement for String.slice
 *
 * @param str - String to perform the operation on
 * @param start - The index to the beginning of the specified portion of stringObj.
 * @param end - The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.
 * @returns
 */
export const slice = (str: string, start?: number, end?: number) =>
    str.slice(start, end);

/**
 * trim
 *
 * Functional replacement for String.trim
 *
 * @param str - String to perform the operation on
 * @returns
 */
export const trim = (str: string) => str.trim();

/**
 * concat
 *
 * Functional replacement for String.trim
 *
 * @param str - String to perform the operation on
 * @param strings - The strings to append to the end of the string.
 * @returns
 */
export const concat = (str: string, ...strings: string[]) =>
    str.concat(...strings);

/* --------------------------------- Helpers -------------------------------- */

/**
 * replace
 *
 * Functional replacement for String.replace
 *
 * @param str - String to perform the operation on
 * @param searchValue - Substring to search for
 * @param replacer - Function that returns the replacement value
 * @returns Replaced string
 */
export const replace = (
    str: string,
    searchValue: string | RegExp,
    replacer: string | ((substring: string) => string),
) => {
    if (typeof replacer === 'string') return str.replace(searchValue, replacer);

    return str.replace(searchValue, replacer);
};

/**
 * properCase
 *
 * Capitalize the first letter of each word
 *
 * @param str - String to perform the operation on
 * @returns The transformed string
 */
export const properCase = (str: string): string =>
    replace(lowerCase(str), /^\w|\s\w/g, upperCase);

/**
 * alphaNumeric
 *
 * Remove non-alphanumeric characters
 *
 * @param str - String to perform the operation on
 * @returns The transformed string
 */
export const alphaNumeric = (str: string): string =>
    replace(str, /[^a-z0-9\s]/gi, '');

/**
 * kebabCase
 *
 * Skewer and lower case each word
 *
 * @param str - String to perform the operation on
 * @returns The transformed string
 */
export const kebabCase = (str: string): string =>
    replace(alphaNumeric(lowerCase(str)), /\s/g, '-');

/**
 * camelToProperCase
 *
 * Converts camel case to proper case
 *
 * @param str - String to perform the operation on
 * @returns
 */
export const camelToProperCase = (str: string) =>
    properCase(replace(str, /([A-Z])/g, ' $1'));

/**
 * truncate
 *
 * Truncate text after a certain amount of characters and append '...'
 *
 * @param str - String to perform the operation on
 * @param maxLength - Max length of the string is allowed to be
 * @returns
 */
export const truncate = (str: string, maxLength: number) => {
    if (str.length < maxLength) return str;

    return concat(trim(slice(str, 0, maxLength)), '...');
};


/**
 * lowerFirstChar
 *
 * Lowercase the first character of a string
 *
 * @param str - String to perform the operation on
 * @returns
 */
export const lowerFirstChar = (str: string) => {
    if (!str) return str;

    return concat(lowerCase(slice(str, 0, 1)), slice(str, 1));
}